/**
 * Central flavor priority and bag distribution for quiz, buybox, and cart copy.
 * Change FLAVOR_DISTRIBUTION_ORDER to update order everywhere.
 */
(function (global) {
  'use strict';

  /** Highest-priority first (extra bags go to earlier entries). */
  const FLAVOR_DISTRIBUTION_ORDER = ['rund', 'kip', 'vis'];

  function getFlavorDistributionOrder() {
    return FLAVOR_DISTRIBUTION_ORDER.slice();
  }

  function getDefaultFlavorId() {
    return FLAVOR_DISTRIBUTION_ORDER[0];
  }

  /**
   * Selected flavors in distribution priority (for display, defaults, cart copy).
   * @param {string[]} selectedFlavors
   * @returns {string[]}
   */
  function orderSelectedFlavors(selectedFlavors) {
    if (!selectedFlavors || !selectedFlavors.length) return [];
    const set = new Set(selectedFlavors);
    return FLAVOR_DISTRIBUTION_ORDER.filter((f) => set.has(f));
  }

  /**
   * Flavors to drop first when selection exceeds max (lowest priority first).
   * @returns {string[]}
   */
  function getFlavorTrimOrder() {
    return FLAVOR_DISTRIBUTION_ORDER.slice().reverse();
  }

  /**
   * Even split with remainder to priority flavors.
   * @param {number} bagCount
   * @param {string[]} selectedFlavors
   * @returns {Record<string, number>}
   */
  function calculateFlavorDistribution(bagCount, selectedFlavors) {
    if (!selectedFlavors || selectedFlavors.length === 0) {
      return {};
    }

    const orderedFlavors = orderSelectedFlavors(selectedFlavors);
    if (orderedFlavors.length === 0) {
      return {};
    }

    const count = Math.max(0, Math.floor(Number(bagCount) || 0));
    if (count === 0) {
      return {};
    }

    const distribution = {};
    const baseBags = Math.floor(count / orderedFlavors.length);
    const remainder = count % orderedFlavors.length;

    orderedFlavors.forEach((flavor) => {
      distribution[flavor] = baseBags;
    });

    for (let i = 0; i < remainder; i++) {
      const flavor = orderedFlavors[i];
      distribution[flavor] = (distribution[flavor] || 0) + 1;
    }

    return distribution;
  }

  /**
   * Flavor ids with qty > 0, in distribution order.
   * @param {Record<string, number>} distribution
   * @returns {string[]}
   */
  function getFlavorsWithPositiveQuantity(distribution) {
    const dist = distribution || {};
    return FLAVOR_DISTRIBUTION_ORDER.filter((f) => (dist[f] || 0) > 0);
  }

  /**
   * @param {Record<string, number>} distribution
   * @param {(flavorId: string, quantity: number) => void} callback
   */
  function forEachFlavorInDistributionOrder(distribution, callback) {
    const dist = distribution || {};
    FLAVOR_DISTRIBUTION_ORDER.forEach((f) => {
      const qty = dist[f] || 0;
      if (qty > 0) callback(f, qty);
    });
  }

  /**
   * Iterate flavor keys from a record (e.g. state.flavors) in distribution order.
   * @param {Record<string, *>} flavorsMap
   * @param {(flavorId: string, entry: *) => void} callback
   */
  function forEachFlavorKeyInOrder(flavorsMap, callback) {
    const map = flavorsMap || {};
    FLAVOR_DISTRIBUTION_ORDER.forEach((f) => {
      if (Object.prototype.hasOwnProperty.call(map, f)) {
        callback(f, map[f]);
      }
    });
  }

  const api = {
    FLAVOR_DISTRIBUTION_ORDER,
    getFlavorDistributionOrder,
    getDefaultFlavorId,
    orderSelectedFlavors,
    getFlavorTrimOrder,
    calculateFlavorDistribution,
    getFlavorsWithPositiveQuantity,
    forEachFlavorInDistributionOrder,
    forEachFlavorKeyInOrder
  };

  global.FlavorDistribution = api;
})(typeof window !== 'undefined' ? window : globalThis);
